import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Checkbox,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { decryptData } from "../../../../crypto";
import { useLanguages } from "../../../../context/LanguagesContext";
import { toast } from "react-toastify";
import socket from "../../../../socket/socket";
import ROUTES_NAME from "../../../../constants/RoutesName";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../../context/ProfileContext";
import { fetchCurrentTimeApi, checkLoginStatus } from "../../../../services/service";
import userAvatar from "../../../../images/userAvatar.png";

export default function InstructionsPageIpMat() {
  const [step, setStep] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down("md"));

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const { selectedLanguage } = useLanguages();

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .catch((err) => alert("Please use F11 for fullscreen"));
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    setAccepted(false);

    const examTime = new Date(sessionStorage.getItem("startDateTime"));
    // if (examTime) {
    //   const currentTime = new Date();
    //   if (currentTime < examTime) {
    //     const timeout = setTimeout(() => {
    //       toast.success("The exam is now live. You can start!", {
    //         toastId: "examTime",
    //       });
    //     }, examTime - currentTime);
    //     return () => clearTimeout(timeout);
    //   }
    // }

    if (examTime && !isNaN(examTime.getTime())) {
      let intervalId;
      let wasLiveOnMount = false;

      // First check immediately on mount/reload
      const initialCheck = async () => {
        try {
          const response = await fetchCurrentTimeApi().catch(() => null);
          if (response && response.data && response.data.currentTime) {
            const rawCurrentTime = response.data.currentTime;
            const formattedCurrentTime = new Date(
              typeof rawCurrentTime === "string" ? rawCurrentTime.replace(" ", "T") : rawCurrentTime
            );
            if (formattedCurrentTime >= examTime) {
              wasLiveOnMount = true;
            }
          }
        } catch (e) {
          console.error("Error doing initial live check:", e);
        }
      };

      // Periodic check every 10 seconds
      const checkTime = async () => {
        try {
          const response = await fetchCurrentTimeApi();
          if (response && response.data && response.data.currentTime) {
            const rawCurrentTime = response.data.currentTime;
            const formattedCurrentTime = new Date(
              typeof rawCurrentTime === "string" ? rawCurrentTime.replace(" ", "T") : rawCurrentTime
            );
            if (formattedCurrentTime >= examTime) {
              if (!wasLiveOnMount) {
                toast.success("The exam is now live. You can start!", {
                  toastId: "examTime",
                });
              }
              if (intervalId) clearInterval(intervalId);
            }
          }
        } catch (error) {
          console.error("Error checking live time:", error);
        }
      };

      const runTimer = async () => {
        await initialCheck();
        if (!wasLiveOnMount) {
          intervalId = setInterval(checkTime, 10000); // 10 seconds
        }
      };

      runTimer();

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [step]);

  const handleStart = async () => {
    if (accepted) {
      const userId = sessionStorage.getItem("userId");
      const examId = sessionStorage.getItem("examId");

      if (userId === "4660") {
        sessionStorage.setItem("loginTime", new Date().getTime());
        sessionStorage.setItem("examStarted", true);
        navigate(ROUTES_NAME.TEST, { replace: true });
        sessionStorage.removeItem("candidateLoggedinToInstructionsPage");
        socket.emit("changeStatus", {
          examId,
          userId,
          status: "Giving",
        });
        toggleFullscreen();
        return;
      }

      try {
        const response = await fetchCurrentTimeApi().catch((err) => {
          if (sessionStorage.getItem("userId") === "4660") {
            return { data: { currentTime: new Date() } };
          }
          throw err;
        });
        const formattedCurrentTime = new Date(response.data.currentTime);
        const startDateTime = new Date(sessionStorage.getItem("startDateTime"));

        if (formattedCurrentTime.getTime() < startDateTime.getTime() && userId !== "4660") {
          toast.warning("You cannot start the exam before the scheduled time.");
          return;
        }

        try {
          const IsLogin = await checkLoginStatus(examId);
          console.log("IsLogin", IsLogin);
          if (IsLogin.data.success !== true) {
            toast.warning("Candidate has already been logged in another device");
            return;
          }
        } catch (error) {
          toast.warning("Candidate has already been logged in another device");
          return;
        }

        sessionStorage.setItem("loginTime", new Date().getTime());

        if (sessionStorage.getItem("subject_selection") === "yes" && sessionStorage.getItem('userId') !== '4660') {
          sessionStorage.setItem("subjectSelection", true);
          navigate(ROUTES_NAME.SUBJECT_SELECTION, { replace: true });
        } else {
          sessionStorage.setItem("examStarted", true);
          navigate(ROUTES_NAME.TEST, { replace: true });
        }

        sessionStorage.removeItem("candidateLoggedinToInstructionsPage");
        socket.emit("changeStatus", {
          examId,
          userId,
          status: "Giving",
        });
        toggleFullscreen();
      } catch (error) {
        if (sessionStorage.getItem("userId") === "4660") return;
        console.error("Error checking time:", error);
        toast.error("Failed to verify exam time.");
      }
    } else {
      toast.warning("Please confirm that you have read the instructions!");
    }
  };

  return (
    <Box sx={{ overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "0px" : "0px",
          height: isMobile ? "auto" : "100vh",
        }}
      >
        {/* SIDEBAR PROFILE (DESKTOP/TABLET) */}
        {!isTabletOrBelow && (
          <Box
            sx={{
              width: "20%",
              borderRight: "1px solid #ddd",
              bgcolor: "#fcfcfc",
              height: "100vh",
              boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "40px 15px",
                gap: "20px",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  // width: "60%",
                  maxWidth: "120px",
                  background: "#E0E0E2",
                  borderRadius: "10px",
                  padding: "4px",
                  display: "flex",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <img
                  src={profile?.image_url || userAvatar}
                  alt="Profile IMG"
                  style={{
                    maxWidth: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "10px",
                    margin: "auto",
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#333" }}
                >
                  {profile?.full_name}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* MOBILE DRAWER */}
        <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
          <Box
            sx={{ width: 250, p: 3, textAlign: "center" }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
          >
            <Box
              sx={{
                width: "80px",
                height: "80px",
                background: "#E0E0E2",
                borderRadius: "10px",
                padding: "4px",
                display: "flex",
                margin: "20px auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src={profile?.image_url || userAvatar}
                alt="Profile IMG"
                style={{
                  maxWidth: "100%",
                  aspectRatio: "1 / 1",
                  borderRadius: "10px",
                  margin: "auto",
                }}
              />
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#333" }}>
              {profile?.full_name}
            </Typography>
            <Divider sx={{ my: 3 }} />
          </Box>
        </Drawer>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            width: isTabletOrBelow ? "100%" : "80%",
            height: isMobile ? "auto" : "100vh",
          }}
        >
          <Box
            sx={{
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                bgcolor: "#cfeef7",
                p: 1.5,
                borderBottom: "1px solid #ddd",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isTabletOrBelow && (
                <IconButton
                  onClick={toggleDrawer(true)}
                  size="small"
                  edge="start"
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                {step === 1 ? "Instructions" : "Other Important Instructions"}
              </Typography>
            </Box>

            {/* SCROLLABLE CONTENT */}
            <Box
              sx={{
                padding: { xs: "12px", sm: 3 },
                overflowY: isMobile ? "visible" : "auto",
                flex: 1,
                minHeight: 0,
                // paddingBottom: { xs: "100px", sm: "80px", md: "80px" },
              }}
            >
              {step !== 1 ? <GeneralInstructions /> : <OtherInstructions />}

              {/* {step === 2 && (
                <Box>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                      <Checkbox
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        sx={{ ml: -1 }}
                      />
                      <Typography
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem" } }}
                      >
                        Declaration by the Candidate:
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        ml: { xs: 0, sm: 4 },
                        pr: 1,
                        pb: 2,
                        fontSize: { xs: "0.85rem", sm: "0.95rem" },
                        color: "#555",
                        fontStyle: "italic",
                      }}
                    >
                      {selectedLanguage === "english"
                        ? "I Have Read And Understood The Instructions. All Computer Hardware Allotted To Me Are In Proper Working Condition. I Declare That No Prohibited Gadget Like Mobile Phone, Bluetooth Devices Etc., Or Any Prohibited Material Is With Me In The Examination Hall. I Agree That If Found Guilty Of Any Malpractice, I May Be Debarred From This Test And/Or Subjected To Disciplinary Action, Which May Include A Ban From Future Tests/Examinations."
                        : "मैंने निर्देश पढ़े और समझे हैं। मुझे आवंटित सभी कंप्यूटर हार्डवेयर ठीक काम कर रहे हैं। मैं घोषित करता हूं कि परीक्षा हॉल में मेरे पास कोई प्रतिबंधित गैजेट जैसे मोबाइल फोन, ब्लूटूथ उपकरण आदि या कोई भी प्रतिबंधित सामग्री नहीं है। मैं सहमत हूं कि यदि किसी भी अनुचित प्रक्रिया का दोषी पाया जाता है, तो मुझे इस परीक्षण से वंचित किया जा सकता है और/या अनुशासनात्मक कार्रवाई के अधीन किया जा सकता है, जिसमें भविष्य के परीक्षणों/परीक्षाओं से प्रतिबंध शामिल हो सकता है।"}
                    </Typography>
                  </Box>
                </Box>
              )} */}
            </Box>

            {/* DECLARATION BOX (FIXED AT BOTTOM) */}
            {step === 2 && (
              <Box
                sx={{
                  bgcolor: "white",
                  borderTop: "1px solid #ddd",
                  px: { xs: 2, sm: 3 },
                  pt: 1.5,
                  pb: 1,
                }}
              >
                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                  <Checkbox
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    sx={{ ml: -1 }}
                  />
                  <Typography
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem" } }}
                  >
                    Declaration by the Candidate:
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    ml: { xs: 0, sm: 4 },
                    pr: 1,
                    pb: 1,
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    color: "#555",
                    fontStyle: "italic",
                  }}
                >
                  {selectedLanguage === "english"
                    ? "I Have Read And Understood The Instructions. All Computer Hardware Allotted To Me Are In Proper Working Condition. I Declare That No Prohibited Gadget Like Mobile Phone, Bluetooth Devices Etc., Or Any Prohibited Material Is With Me In The Examination Hall. I Agree That If Found Guilty Of Any Malpractice, I May Be Debarred From This Test And/Or Subjected To Disciplinary Action, Which May Include A Ban From Future Tests/Examinations."
                    : "मैंने निर्देश पढ़े और समझे हैं। मुझे आवंटित सभी कंप्यूटर हार्डवेयर ठीक काम कर रहे हैं। मैं घोषित करता हूं कि परीक्षा हॉल में मेरे पास कोई प्रतिबंधित गैजेट जैसे मोबाइल फोन, ब्लूटूथ उपकरण आदि या कोई भी प्रतिबंधित सामग्री नहीं है। मैं सहमत हूं कि यदि किसी भी अनुचित प्रक्रिया का दोषी पाया जाता है, तो मुझे इस परीक्षण से वंचित किया जा सकता है और/या अनुशासनात्मक कार्रवाई के अधीन किया जा सकता है, जिसमें भविष्य के परीक्षणों/परीक्षाओं से प्रतिबंध शामिल हो सकता है।"}
                </Typography>
              </Box>
            )}

            {/* FOOTER BUTTONS */}
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: "flex",
                justifyContent: step === 2 ? "space-between" : "flex-end",
                borderTop: "1px solid #ddd",
                // position: "fixed",
                // bottom: 0,
                // left: isTabletOrBelow ? 0 : "20%",
                // right: 0,
                bgcolor: "white",
                zIndex: 1100,
                boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
                // width: isTabletOrBelow ? "100%" : "80%",
                boxSizing: "border-box",
              }}
            >
              {step === 2 && (
                <Button
                  variant="outlined"
                  onClick={() => setStep(1)}
                  startIcon={<ArrowBackIosIcon />}
                  sx={{
                    borderColor: "black",
                    color: "black",
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: { xs: "14px", sm: "16px" },
                    padding: { xs: "6px 16px", sm: "8px 24px" },
                    "&:hover": {
                      borderColor: "#104a77",
                      backgroundColor: "#f5f8fa",
                      color: "#104a77",
                    },
                  }}
                >
                  Previous
                </Button>
              )}

              {step === 1 && (
                <Button
                  variant="contained"
                  onClick={() => setStep(2)}
                  endIcon={<ArrowForwardIosIcon />}
                  sx={{
                    backgroundColor: "#104a77",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: { xs: "14px", sm: "16px" },
                    padding: { xs: "8px 24px", sm: "10px 32px" },
                  }}
                >
                  Next
                </Button>
              )}

              {step === 2 && (
                <Button
                  variant="contained"
                  disabled={!accepted}
                  onClick={handleStart}
                  sx={{
                    backgroundColor: "#104a77",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: { xs: "13px", sm: "15px" },
                    padding: { xs: "10px 24px", sm: "12px 36px" },
                    textTransform: "none",
                  }}
                >
                  I am ready to begin
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ================= COMPONENTS ================= */

function GeneralInstructions() {
  const [examInstructions, setExamInstructions] = useState();
  const { selectedLanguage } = useLanguages();

  useEffect(() => {
    if (selectedLanguage === "english") {
      setExamInstructions(
        decryptData(sessionStorage.getItem("genralInstructionEn")),
      );
      // setPopupInstructions(
      // "I Have Read And Understood The Instructions. All Computer Hardware Allotted To Me Are In Proper Working Condition. I Declare That No Prohibited Gadget Like Mobile Phone, Bluetooth Devices Etc., Or Any Prohibited Material Is With Me In The Examination Hall. I Agree That If Found Guilty Of Any Malpractice, I May Be Debarred From This Test And/Or Subjected To Disciplinary Action, Which May Include A Ban From Future Tests/Examinations."
      // );
    }
    if (selectedLanguage === "hindi") {
      setExamInstructions(
        decryptData(sessionStorage.getItem("genralInstructionHi")),
      );
      // setPopupInstructions(
      // "मैंने निर्देश पढ़े और समझे हैं। मुझे आवंटित सभी कंप्यूटर हार्डवेयर ठीक काम कर रहे हैं। मैं घोषित करता हूं कि परीक्षा हॉल में मेरे पास कोई प्रतिबंधित गैजेट जैसे मोबाइल फोन, ब्लूटूथ उपकरण आदि या कोई भी प्रतिबंधित सामग्री नहीं है। मैं सहमत हूं कि यदि किसी भी अनुचित प्रक्रिया का दोषी पाया जाता है, तो मुझे इस परीक्षण से वंचित किया जा सकता है और/या अनुशासनात्मक कार्रवाई के अधीन किया जा सकता है, जिसमें भविष्य के परीक्षणों/परीक्षाओं से प्रतिबंध शामिल हो सकता है।"
      // );
    }
  }, [selectedLanguage]);

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: "10px 20px 60px 20px",
          fontSize: "1rem",
          lineHeight: 1.6,

          /* Scrollbar */
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            boxShadow: "inset 0 0 5px grey",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#4565B7",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#39476B",
          },

          /* Typography */
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            margin: "16px 0 8px",
            lineHeight: 1.3,
          },
          "& p": {
            margin: "8px 0",
          },
          "& ul, & ol": {
            paddingLeft: "24px",
          },
          "& li": {
            margin: "4px 0",
          },

          /* ✅ Image Fix */
          "& img": {
            display: "block",
            maxWidth: "100%",
            height: "auto",
            objectFit: "contain",
          },

          /* Media Query */
          "@media (min-width: 800px) and (max-width: 898px)": {
            fontSize: "0.7rem",

            "& h1, & h2, & h3, & h4, & h5, & h6": {
              margin: "12px 0 6px",
            },
            "& ul, & ol": {
              paddingLeft: "18px",
            },
          },
        }}
        dangerouslySetInnerHTML={{ __html: examInstructions }}
      />
    </>
  );
}

function OtherInstructions() {
  const [examInstructions, setExamInstructions] = useState();
  const { selectedLanguage } = useLanguages();

  useEffect(() => {
    if (selectedLanguage === "english") {
      setExamInstructions(
        decryptData(sessionStorage.getItem("examInstructionEn")),
      );
      // setPopupInstructions(
      //   "I Have Read And Understood The Instructions. All Computer Hardware Allotted To Me Are In Proper Working Condition. I Declare That No Prohibited Gadget Like Mobile Phone, Bluetooth Devices Etc., Or Any Prohibited Material Is With Me In The Examination Hall. I Agree That If Found Guilty Of Any Malpractice, I May Be Debarred From This Test And/Or Subjected To Disciplinary Action, Which May Include A Ban From Future Tests/Examinations."
      // );
    }
    if (selectedLanguage === "hindi") {
      setExamInstructions(
        decryptData(sessionStorage.getItem("examInstructionHi")),
      );
      // setPopupInstructions(
      //   "मैंने निर्देश पढ़े और समझे हैं। मुझे आवंटित सभी कंप्यूटर हार्डवेयर ठीक काम कर रहे हैं। मैं घोषित करता हूं कि परीक्षा हॉल में मेरे पास कोई प्रतिबंधित गैजेट जैसे मोबाइल फोन, ब्लूटूथ उपकरण आदि या कोई भी प्रतिबंधित सामग्री नहीं है। मैं सहमत हूं कि यदि किसी भी अनुचित प्रक्रिया का दोषी पाया जाता है, तो मुझे इस परीक्षण से वंचित किया जा सकता है और/या अनुशासनात्मक कार्रवाई के अधीन किया जा सकता है, जिसमें भविष्य के परीक्षणों/परीक्षाओं से प्रतिबंध शामिल हो सकता है।"
      // );
    }
  }, [selectedLanguage]);

  return (
    <>
      {/* import { Box } from "@mui/material"; */}

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: "10px 20px 60px 20px",
          fontSize: "1rem",
          lineHeight: 1.6,

          /* Scrollbar */
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            boxShadow: "inset 0 0 5px grey",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#4565B7",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#39476B",
          },

          /* Typography */
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            margin: "16px 0 8px",
            lineHeight: 1.3,
          },
          "& p": {
            margin: "8px 0",
          },
          "& ul, & ol": {
            paddingLeft: "24px",
          },
          "& li": {
            margin: "4px 0",
          },

          /* ✅ Image Fix */
          "& img": {
            display: "block",
            maxWidth: "100%",
            height: "auto",
            objectFit: "contain",
          },

          /* Media Query */
          "@media (min-width: 800px) and (max-width: 898px)": {
            fontSize: "0.7rem",

            "& h1, & h2, & h3, & h4, & h5, & h6": {
              margin: "12px 0 6px",
            },
            "& ul, & ol": {
              paddingLeft: "18px",
            },
          },
        }}
        dangerouslySetInnerHTML={{ __html: examInstructions }}
      />
    </>
  );
}
